/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import React from "react";
import {
  Autocomplete,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { renderQuestionIdentityOptionWithTypeChip } from "./form-builder-question-autocomplete-option";
import { BuilderSection } from "./form-builder-utils";
import { getSectionQuestionOptions, QuestionGroupsBySection } from "./form-builder-shared";
import {
  isValidSectionIdentifier,
  SECTION_IDENTIFIER_RULE,
} from "./question-identifier-validation";

type Props = {
  section: BuilderSection;
  sections: BuilderSection[];
  questionIdentityLabelMap: Record<string, string>;
  questionIdentityTypeMap: Record<string, string>;
  questionGroupsBySection: QuestionGroupsBySection;
  onUpdateSection: (
    sectionId: string,
    update: (section: BuilderSection) => BuilderSection,
  ) => void;
  onDeleteSection: (sectionId: string) => void;
};

const FormBuilderSectionCard: React.FC<Props> = ({
  section,
  sections,
  questionIdentityLabelMap,
  questionIdentityTypeMap,
  questionGroupsBySection,
  onUpdateSection,
  onDeleteSection,
}) => {
  const sectionName = section.name.trim();
  const sectionTitle = section.title.trim();
  const sectionDescription = section.description.trim();
  const sectionNameInvalid = Boolean(sectionName) && !isValidSectionIdentifier(sectionName);
  const sectionTitleMissing = sectionTitle.length === 0;
  const sectionDescriptionMissing = sectionDescription.length === 0;

  return (
    <Card key={section.draftId} variant="outlined">
      <CardContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Section Name"
                  value={section.name}
                  onChange={(event) => {
                    onUpdateSection(section.draftId, (current) => ({
                      ...current,
                      name: event.target.value,
                    }));
                  }}
                  error={sectionNameInvalid}
                  helperText={sectionNameInvalid ? SECTION_IDENTIFIER_RULE : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Section Title"
                  value={section.title}
                  onChange={(event) => {
                    onUpdateSection(section.draftId, (current) => ({
                      ...current,
                      title: event.target.value,
                    }));
                  }}
                  error={sectionTitleMissing}
                  helperText={sectionTitleMissing ? "Required" : ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  multiline
                  minRows={3}
                  label="Description"
                  value={section.description}
                  onChange={(event) => {
                    onUpdateSection(section.draftId, (current) => ({
                      ...current,
                      description: event.target.value,
                    }));
                  }}
                  error={sectionDescriptionMissing}
                  helperText={sectionDescriptionMissing ? "Required" : ""}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Grid container spacing={1} alignItems="flex-start">
              <Grid size={{ xs: 12, sm: 11 }}>
                <Autocomplete
                  multiple
                  options={getSectionQuestionOptions(
                    section.draftId,
                    section.questionKeys,
                    sections,
                    questionGroupsBySection.sectionAssignableIdentityOptions,
                  )}
                  value={section.questionKeys}
                  getOptionLabel={(optionKey) => questionIdentityLabelMap[optionKey] || optionKey}
                  renderOption={renderQuestionIdentityOptionWithTypeChip(
                    questionIdentityLabelMap,
                    questionIdentityTypeMap,
                  )}
                  onChange={(_, newValue) => {
                    onUpdateSection(section.draftId, (current) => ({
                      ...current,
                      questionKeys: newValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Section Questions"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  aria-label="Delete section"
                  onClick={() => onDeleteSection(section.draftId)}
                >
                  <DeleteOutline />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default FormBuilderSectionCard;
